import { ReactNode } from 'react';

import { Text } from '@northladder/i18n';
import { TTextLocalized } from '@northladder/utilities';

import { cn } from '../../../lib';
import { TableCell, TableRow } from '../../../ui';

interface ISimpleTableRowValueBasic {
  className?: string;
  isFontMono?: boolean;
  isMissing?: boolean;
  prefix?: string | ReactNode;
  suffix?: string | null;
  text: TTextLocalized;
  type: 'basic';
}

interface ISimpleTableRowValueCustom {
  element: ReactNode;
  type: 'custom';
}

type TSimpleTableRowValue =
  | ISimpleTableRowValueBasic
  | ISimpleTableRowValueCustom;

export interface ISimpleTableRowOptions {
  id: string;
  label: TTextLocalized;
  labelSuffix?: TTextLocalized;
  value: TSimpleTableRowValue;
}

export interface ISimpleTableRowProps extends ISimpleTableRowOptions {
  hasFaintBorders: boolean;
  index: number;
}

interface IRenderSimpleTableRowCellProps {
  value: TSimpleTableRowValue;
}

/**
 * -----------------------------------------------------------------------------
 * Renders a single cell of the simple table row based on the passed `type`
 */
function RenderSimpleTableRowCell({ value }: IRenderSimpleTableRowCellProps) {
  if (value?.type === 'basic') {
    const { className, isFontMono, isMissing, prefix, suffix, text } = value;

    // This will allow to show the fallback label when the item is missing.
    if (isMissing) {
      return (
        <span className={cn('text-text-caption font-mono', className)}>
          <Text className={cn(isFontMono && 'font-mono')} text={text} />
        </span>
      );
    }

    return (
      <span className="flex flex-row items-center justify-end gap-1">
        {prefix ? (
          <span className={cn('text-text-caption font-mono')}>{prefix}</span>
        ) : null}
        <Text
          className={cn(
            'text-wrap w-fit',
            isFontMono && 'font-mono',
            className
          )}
          text={text}
        />
        {suffix ? (
          <span className={cn('text-text-caption font-mono')}>{suffix}</span>
        ) : null}
      </span>
    );
  }

  const { element } = value;

  return element;
}

/**
 * -----------------------------------------------------------------------------
 * Renders a single row of the simple table.
 */
export function SimpleTableRow({
  hasFaintBorders,
  id,
  index,
  label,
  labelSuffix,
  value,
}: ISimpleTableRowProps) {
  return (
    <TableRow
      className={cn(
        'w-full',
        index % 2 && 'bg-gray-300/10 dark:bg-gray-800/30',
        index % 2 && hasFaintBorders && 'bg-gray-300/5 dark:bg-gray-800/5',
        hasFaintBorders && '!border-border/40 dark:!border-border/20'
      )}
      id={id}
    >
      <TableCell
        className="
          flex w-auto flex-wrap items-center justify-start gap-2 px-6 py-4
          text-start sm:flex-nowrap
        "
      >
        <Text className="w-fit sm:flex-nowrap" text={label} />
        {labelSuffix ? (
          <Text
            className="text-text-caption text-wrap w-full font-mono text-xs"
            text={labelSuffix}
          />
        ) : null}
      </TableCell>

      <TableCell className="w-fit space-x-1 px-6 py-4 text-end">
        <RenderSimpleTableRowCell value={value} />
      </TableCell>
    </TableRow>
  );
}
